import { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { FontSizes, FontWeights, Modal, Text } from '@calm-web/design-system';

import { ShareContext } from '@/components/app/shareFreeContent/ShareProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useDeviceState } from '@/hooks/store';
import { Guide } from '@/store/guide/types';

import { messages } from './messages';
import { CopyShareLinkField, ShareTitleComponent } from './styles';

interface ShareModalProps {
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	isModalOpen: boolean;
	currentGuide?: Guide | null;
	shareUrlOverrides?: string;
}

export interface AlertNotice {
	isSuccess: boolean;
	message: string;
}

const ShareModal = ({ isModalOpen, setIsModalOpen, shareUrlOverrides }: ShareModalProps) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const { isMobile } = useDeviceState();

	const { shareUrl: guideShareUrl, senderShareToken: shareToken } = useContext(ShareContext);
	const shareLinkText: string = formatMessage(messages.shareLinkText);

	const shareUrl = shareUrlOverrides ?? guideShareUrl;

	const onClick = async () => {
		logEvent({
			eventName: 'Share Link : Copied',
			eventProps: {
				share_token: shareToken,
			},
		});
	};

	const onClose = () => {
		logEvent({
			eventName: 'Screen : Exited',
			eventProps: {
				screen: 'Modal',
				type: 'New Feature Share',
			},
		});
		setIsModalOpen(false);
	};

	useEffect(() => {
		if (isMobile && typeof navigator.share === 'function' && shareUrl && isModalOpen) {
			setIsModalOpen(false);
			navigator
				.share({
					url: shareUrl,
					text: shareLinkText,
				})
				.catch(() => {});
		}
	}, [isModalOpen, setIsModalOpen, shareUrl, shareLinkText, isMobile]);

	useEffect(() => {
		if (isModalOpen) {
			logEvent({
				eventName: 'Screen : Viewed',
				eventProps: {
					screen: 'Modal',
					type: 'New Feature Share',
				},
			});
		}
	}, [isModalOpen, logEvent]);

	function renderCopyLinkField() {
		return (
			<CopyShareLinkField
				colorTheme="light"
				url={shareUrl || ''}
				onClick={onClick}
				notificationBackgroundColor="noticeBgSuccess"
				successMessage={formatMessage(messages.linkCopyConfirm)}
				label={formatMessage(messages.copy)}
			>
				<Text
					noMargin
					color="gray5"
					el="p"
					size={FontSizes.base}
					weight={FontWeights.Regular}
					id="shareUrlDisplay"
				>
					{shareUrl}{' '}
				</Text>
			</CopyShareLinkField>
		);
	}

	return (
		<Modal
			isOpen={isModalOpen}
			closeModal={onClose}
			canClose
			hasOverflowY={false}
			aria-label="messages.modalTitle"
		>
			<>
				<ShareTitleComponent>{formatMessage(messages.share)}</ShareTitleComponent>
				<Text align="center" noMargin color="gray5" el="p" size={FontSizes.base} weight={FontWeights.Regular}>
					{formatMessage(messages.shareModalMessage)}
				</Text>

				{renderCopyLinkField()}
			</>
		</Modal>
	);
};

export default ShareModal;
